<template>
  <div class="form-component" v-if="form">
    <p class="alert alert-info" v-if="form.helpTitle" v-html="form.helpTitle">
    </p>
    <p v-if="expiredSession" class="alert alert-danger text-center">
      <i class="fas fa-exclamation-triangle"></i>
      Unauthorized access. Please log in to continue.
    </p>
    <div class="card card-primary" :style="{ border: isModal ? 'none' : '' }">
      <template v-if="!isModal">
        <slot name="title">
          <div class="card-header">
            <span v-if="form.icon" class="fa" :class="form.icon"></span>
            {{ form.title }}
          </div>
        </slot>
      </template>
      <div class="card-body" :class="{ 'p-0': isModal }">
        <div class="form-section-amber" v-show="form.info">{{ form.info }}</div>
        <form :class="{ 'form-horizontal': isHorizontal }" @submit.prevent="submitForm($event)" ref="form" enctype="multipart/form-data">
          <template v-if="form.disableAutofill">
            <input type="text" style="display: none;"/>
            <input type="password" style="display: none;"/>
          </template>
          <input type="hidden" name="permalink" :value="form.permalink"/>
          <template v-for="(section, index) in form.sections">
            <div v-if="section.html" v-html="section.html" :key="index"></div>
            <component :key="index" v-if="!section.html"
              :is="parseType(section)"
              :options="section"
              v-model="section.value"
              @input="runWatcher($event, section.name)"
              ref="fields">
            </component>
          </template>
          <transition name="expand">
            <div class="row" v-show="formError">
              <div class="col-sm-8 offset-sm-4">
                <div class="form-section-danger">{{ formError }}</div>
              </div>
            </div>
          </transition>
          <div class="form-group row" v-if="!isModal">
            <slot name="actions">
              <div class="col-sm-8 offset-sm-4">
                <button class="btn btn-primary" type="submit">{{ form.submit }}</button>
              </div>
            </slot>
          </div>
        </form>
      </div>
      <div class="form-links" v-if="form.links">
        <p v-html="form.links"></p>
      </div>
      <div class="form-footer" v-if="form.footer">
        <h2>{{ form.footer.title }}</h2>
        <p v-html="form.footer.html"></p>
      </div>
    </div>
  </div>
</template>

<script>
/* global tinymce:true */
import FormSection from '@/components/FormSection.vue'
// Inputs
import AccountSelectInput from '@/components/inputs/AccountSelectInput'
import AddressInput from '@/components/inputs/AddressInput'
import AgreementInput from '@/components/inputs/AgreementInput'
import ListSelectInput from '@/components/inputs/ListSelectInput'
import AmazonImageInput from '@/components/inputs/AmazonImageInput'
import AmazonMediaInput from '@/components/inputs/AmazonMediaInput'
import AmazonMediaMultipleInput from '@/components/inputs/AmazonMediaMultipleInput'
import BooleanInput from '@/components/inputs/BooleanInput'
import CalendarInput from '@/components/inputs/CalendarInput'
import CarChangeInput from '@/components/inputs/CarChangeInput'
import CheckboxesInput from '@/components/inputs/CheckboxesInput'
import CheckboxInput from '@/components/inputs/CheckboxInput'
import CreditCardInput from '@/components/inputs/CreditCardInput'
import CurrencyInput from '@/components/inputs/CurrencyInput'
import CustomChargeInput from '@/components/inputs/CustomChargeInput'
import CustomChargesInput from '@/components/inputs/CustomChargesInput'
import DateDurationInput from '@/components/inputs/DateDurationInput'
import DownloadInput from '@/components/inputs/DownloadInput'
import DateRangeInput from '@/components/inputs/DateRangeInput'
import DateTimeRangeInput from '@/components/inputs/DateTimeRangeInput'
import DayRangeInput from '@/components/inputs/DayRangeInput'
import DisplayInput from '@/components/inputs/DisplayInput'
import EmailInput from '@/components/inputs/EmailInput'
import EmailsInput from '@/components/inputs/EmailsInput'
import EmailTemplateInput from '@/components/inputs/EmailTemplateInput'
import EmailTypeInput from '@/components/inputs/EmailTypeInput'
import GitLabMediaMultipleInput from '@/components/inputs/GitLabMediaMultipleInput'
import HiddenInput from '@/components/inputs/HiddenInput'
import NumberInput from '@/components/inputs/NumberInput'
import ObjectSelectInput from '@/components/inputs/ObjectSelectInput'
import ObjectsSelectInput from '@/components/inputs/ObjectsSelectInput'
import OneWayTripInput from '@/components/inputs/OneWayTripInput'
import PasswordConfirmInput from '@/components/inputs/PasswordConfirmInput'
import PasswordInput from '@/components/inputs/PasswordInput'
import PaymentMethodInput from '@/components/inputs/PaymentMethodInput'
import PriceBreakdown from '@/components/displays/PriceBreakdown'
import PercentageInput from '@/components/inputs/PercentageInput'
import PhoneInput from '@/components/inputs/PhoneInput'
import PostcodeInput from '@/components/inputs/PostcodeInput'
import ReservationPromoInput from '@/components/inputs/ReservationPromoInput'
import RichTextInput from '@/components/inputs/RichTextInput'
import SearchInput from '@/components/inputs/SearchInput'
import SmartcardSelectInput from '@/components/inputs/SmartcardSelectInput'
import SummaryDisplayInput from '@/components/inputs/SummaryDisplayInput'
import TextareaInput from '@/components/inputs/TextareaInput'
import TextInput from '@/components/inputs/TextInput'
import TextSelectInput from '@/components/inputs/TextSelectInput'
import TextsSelectInput from '@/components/inputs/TextsSelectInput'
import TimeInput from '@/components/inputs/TimeInput'
import UrlInput from '@/components/inputs/UrlInput'
import UsernameInput from '@/components/inputs/UsernameInput'
import PromoCodeInput from '@/components/inputs/PromoCodeInput'
// Displays
import UsernameDisplay from '@/components/displays/UsernameDisplay.vue'
import PasswordDisplay from '@/components/displays/PasswordDisplay.vue'
import TextDisplay from '@/components/displays/TextDisplay.vue'
import UrlDisplay from '@/components/displays/UrlDisplay.vue'
import EmailDisplay from '@/components/displays/EmailDisplay.vue'
import PhoneDisplay from '@/components/displays/PhoneDisplay.vue'
import NumberDisplay from '@/components/displays/NumberDisplay.vue'
import BucketDisplay from '@/components/displays/BucketDisplay.vue'
import AddressDisplay from '@/components/displays/AddressDisplay.vue'
import CurrencyDisplay from '@/components/displays/CurrencyDisplay.vue'
import AmazonImageDisplay from '@/components/displays/AmazonImageDisplay.vue'
import ListDisplay from '@/components/displays/ListDisplay.vue'
import dom from '@/util/dom'
import { OPEN_ERROR_MODAL, SUBMIT_FORM, LOAD_FORM, OPEN_CONFIRMATION_MODAL, CLOSE_FORM } from '@/vuex/types'
import { mapGetters } from 'vuex'

export default {
  name: 'VueForm',
  components: {
    // inputs
    AccountSelectInput,
    AddressInput,
    AgreementInput,
    AmazonImageInput,
    AmazonMediaInput,
    AmazonMediaMultipleInput,
    BooleanInput,
    CalendarInput,
    CarChangeInput,
    CheckboxesInput,
    CheckboxInput,
    CreditCardInput,
    CurrencyInput,
    CustomChargeInput,
    CustomChargesInput,
    DateDurationInput,
    DownloadInput,
    DateRangeInput,
    DateTimeRangeInput,
    DayRangeInput,
    DisplayInput,
    EmailInput,
    EmailsInput,
    EmailTemplateInput,
    EmailTypeInput,
    GitLabMediaMultipleInput,
    HiddenInput,
    ListSelectInput,
    NumberInput,
    ObjectSelectInput,
    ObjectsSelectInput,
    OneWayTripInput,
    PasswordConfirmInput,
    PasswordInput,
    PaymentMethodInput,
    PercentageInput,
    PhoneInput,
    PostcodeInput,
    PriceBreakdown,
    ReservationPromoInput,
    RichTextInput,
    SearchInput,
    SmartcardSelectInput,
    SummaryDisplayInput,
    TextareaInput,
    TextInput,
    TextSelectInput,
    TextsSelectInput,
    TimeInput,
    UrlInput,
    UsernameInput,
    PromoCodeInput,
    // displays
    UsernameDisplay,
    PasswordDisplay,
    TextDisplay,
    UrlDisplay,
    EmailDisplay,
    PhoneDisplay,
    NumberDisplay,
    BucketDisplay,
    AddressDisplay,
    CurrencyDisplay,
    AmazonImageDisplay,
    ListDisplay,
    FormSection
  },
  props: {
    isHorizontal: {
      type: Boolean,
      default () {
        return true
      }
    },
    options: {
      type: Object,
      default () {
        return {
          permalink: false,
          title: 'Loading Form...',
          submit: 'Loading...',
          submitting: 'Processing...',
          confirmation: false,
          redirect: '/',
          params: false,
          disableAutofill: false
        }
      }
    },
    sections: {
      type: Array,
      default () {
        return []
      }
    },
    isModal: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      watchers: [],
      validForm: false,
      formError: false,
      sending: false,
      loadedForm: false
    }
  },
  computed: {
    form () {
      if (this.loadedForm) {
        return this.$store.getters.getForm(this.loadedForm)
      } else {
        return { ...this.options, sections: this.sections }
      }
    },
    ...mapGetters([
      'expiredSession'
    ])
  },
  watch: {
    async $route () {
      this.loadedForm = false
      let permalink = this.options.permalink || this.$route.meta.vueForm
      this.$store.commit(CLOSE_FORM, permalink)
      let params = {
        ...this.options.params,
        ...this.$route.params
      }
      if (permalink && this.options.method !== 'search') {
        let { meta } = Array.from(this.$route.matched).reverse().find(m => m.meta.authType)
        try {
          await this.$store.dispatch(LOAD_FORM, {
            permalink,
            params,
            auth: meta.authType
          })
          this.loadedForm = permalink
          await this.$nextTick()
          // attach listeners
          let watchers = []
          for (let child of this.$children) {
            if (child.options && child.options.watchers) {
              for (let watcher of child.options.watchers) {
                if (!Array.isArray(watchers[watcher])) watchers[watcher] = []
                watchers[watcher].push({
                  el: child,
                  name: watcher
                })
              }
            }
          }
          this.watchers = watchers
          await this.$nextTick()
          for (let name in this.watchers) {
            let section = this.form.sections.find(el => el.name === name)
            for (let watcher of this.watchers[name]) {
              watcher.el.runWatcher(section.value, section.name)
            }
          }
          this.$emit('load')
        } catch (err) {
          this.$store.dispatch(OPEN_ERROR_MODAL, err)
        }
      }
    }
  },
  async mounted () {
    let permalink = this.options.permalink || this.$route.meta.vueForm
    this.$store.commit(CLOSE_FORM, permalink)
    let params = {
      ...this.options.params,
      ...this.$route.params
    }
    if (permalink && this.options.method !== 'search') {
      let { meta } = Array.from(this.$route.matched).reverse().find(m => m.meta.authType)
      try {
        await this.$store.dispatch(LOAD_FORM, {
          permalink,
          params,
          auth: meta.authType
        })
        this.loadedForm = permalink
        await this.$nextTick()
        // attach listeners
        let watchers = []
        for (let child of this.$children) {
          if (child.options && child.options.watchers) {
            for (let watcher of child.options.watchers) {
              if (!Array.isArray(watchers[watcher])) watchers[watcher] = []
              watchers[watcher].push({
                el: child,
                name: watcher
              })
            }
          }
        }
        this.watchers = watchers
        await this.$nextTick()
        for (let name in this.watchers) {
          let section = this.form.sections.find(el => el.name === name)
          for (let watcher of this.watchers[name]) {
            watcher.el.runWatcher(section.value, section.name)
          }
        }
        this.$emit('load')
      } catch (err) {
        this.$store.dispatch(OPEN_ERROR_MODAL, err)
      }
    }
  },
  methods: {
    async submitForm (e) {
      this.$emit('submit', e)
      this.sending = true
      await this.validateForm()
      if (this.validForm && this.options.method !== 'search') {
        let { meta } = Array.from(this.$route.matched).reverse().find(m => m.meta.authType)
        tinymce.triggerSave()
        let formData = this.serializeForm()
        let method = this.options.method || this.$route.meta.vueFormMethod
        this.formError = false
        formData.params = this.$route.params
        this.options.submit = this.options.submitting
        try {
          await this.$store.dispatch(SUBMIT_FORM, { data: formData, method, auth: meta.authType })
          this.sending = false
          if (this.form && this.form.confirmation) {
            this.$store.dispatch(OPEN_CONFIRMATION_MODAL, this.form)
          } else if (this.form && this.form.redirect) {
            this.$router.push(this.form.redirect)
            this.$emit('complete')
          } else if (this.options && this.options.redirect) {
            this.$router.push(this.options.redirect)
            this.$emit('complete')
          } else {
            this.$emit('complete')
          }
        } catch (err) {
          this.$store.dispatch(OPEN_ERROR_MODAL, err)
        }
      }
      this.sending = false
    },
    serializeForm () {
      let form = { elements: this.form.sections }
      let formData = dom(form).serializeForm()
      return { ...formData, permalink: this.form.permalink }
    },
    async validateForm () {
      let validForm = true
      for (let el of this.$refs.fields) {
        if (el.options) {
          if (el.options.type === 'number' && el.options.value === '') {
            el.options.value = null
          }
          if (el.options.required || el.required || !el.isValid) {
            await el.validate()
            if (validForm) {
              validForm = el.isValid
            }
          }
        }
      }
      this.validForm = validForm
    },
    parseType ({ type, objectType }) {
      switch (type) {
        case 'form-section':
          return 'form-section'
        case 'loose-search':
          return 'search-input'
        case 'object':
        case 'relation':
          if (objectType === 'loose-search') {
            return 'search-input'
          } else if (objectType) {
            return `${objectType}-input`
          } else {
            return 'search-input'
          }
        case 'price-breakdown':
          return 'price-breakdown'
        default:
          return type + '-input'
      }
    },
    runWatcher (val, name) {
      if (!this.sending) {
        this.$emit('updated')
      }
      for (let watcherName in this.watchers) {
        let section = this.form.sections.find(el => el.name === name)
        for (let watcher of this.watchers[watcherName]) {
          watcher.el.runWatcher(section.value, section.name)
        }
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/Form.vue